﻿import React, { useContext, useState, useEffect } from "react";

import { BookingFlowContext } from "../BookingFlowContext";
import { validateFormObject } from "../Validation";
import { formatCurrency } from "../Currency";
import { products, payment } from "../Endpoints";
import { paramsToObject } from "../../../../../Resources/Scripts/_Shared/history";

import { Heading } from "../partials/Heading";
import { BackNavigation } from "../partials/BackNavigation";
import {
    appliedPromotionConfig,
    hasSalesOrderAttachment,
    isMonthsFreeOrHalfPricePromotion,
    isSalesOrderAttachmentRequired,
} from "../util";
import { Lightbox } from "../partials/Lightbox";
import { List } from "../partials/List";
import { Error } from "../../../../../Resources/Scripts/_Shared/elements/Error";
import { parseDate } from "../../../../../Resources/Scripts/_Shared/parseDate";

import { CheckboxWithLabel } from "../../../../../Resources/Scripts/_Shared/elements/CheckboxWithLabel";

import OrderOverviewDiscount from "../partials/OrderOverviewDiscount";

const emptyGuid = "00000000-0000-0000-0000-000000000000";

const OrderOverview = (props) => {
    const { bookingFlowContext, setBookingFlowContext } =
        useContext(BookingFlowContext);
    const formLang = {
        ...bookingFlowContext.episerver,
        ...bookingFlowContext.localization.global.cost,
        ...bookingFlowContext.localization.onlineBooking,
        ...bookingFlowContext.localization.orderOverview,
    };

    const locale = bookingFlowContext.country;
    const selectedLocale =
        locale === "no" ? "nb" : locale === "se" ? "sv" : locale;

    const onlineBookingFormData =
        bookingFlowContext.availableSteps.onlineBooking.formData;
    const productSelection = onlineBookingFormData.productSelection.item;
    const unfilteredAccessDevices = [
        ...(onlineBookingFormData.accessDevices ?? []),
        ...(onlineBookingFormData.accessDevicesOptional ?? []),
    ];
    const accessDevices = unfilteredAccessDevices.map((ad) => {
        return {
            ProductOption: ad.ProductOption,
            Quantity: ad.Quantity,
            BillingFrequency: ad.BillingFrequency,
            Spot_Name: ad.spotName ? ad.spotName.join(";") : null,
        };
    });
    const accessDeviceKeys = accessDevices
        .map((accessDevice) => accessDevice.Id)
        .join("-");

    const paymentSelection = onlineBookingFormData.paymentSelection?.item;
    const paymentSelectionValue =
        formLang.productType === 1 ? paymentSelection.value : "N/A";
    const isFlexbookingProduct = productSelection.ProcessType === "Flexbooking";

    const attachmentMissing =
        isSalesOrderAttachmentRequired(bookingFlowContext) &&
        !hasSalesOrderAttachment(bookingFlowContext);

    const startDateSelection = onlineBookingFormData.date.date;
    const startTimeSelection = onlineBookingFormData.time?.text;
    const endDateSelection = onlineBookingFormData.endDate?.date;
    const endTimeSelection = onlineBookingFormData.endTime?.text;

    const vehiclesFormData =
        bookingFlowContext.availableSteps.addVehicle.formData;
    const vehicleLicensePlateNumbers =
        vehiclesFormData && vehiclesFormData.length > 0
            ? vehiclesFormData
                  .map((item) => item.PlateText.replace(/;/g, ""))
                  .join(";")
            : "";
    const vehicleDescriptions =
        vehiclesFormData && vehiclesFormData.length > 0
            ? vehiclesFormData
                  .map((item) => (item.descriptionText ?? "").replace(/;/g, ""))
                  .join(";")
            : "";

    const spotSelectionFormData =
        productSelection.Type === "RESERVED"
            ? onlineBookingFormData.spotSelection.filter(
                  (spot) => spot.item.ParkingSpace.Uid !== emptyGuid,
              )
            : [];
    const selectedSpotsList =
        spotSelectionFormData.length > 0
            ? spotSelectionFormData
                  .map((item) => item.item.ParkingSpace.Name)
                  .join(", ")
            : "N/A";

    const insuranceFeeId = onlineBookingFormData.insuranceFee?.selected
        ? productSelection.CancellationInsuranceFee.Id
        : null;
    const numberOfSpots =
        productSelection.Type === "RESERVED"
            ? spotSelectionFormData.length
            : onlineBookingFormData.numberOfParkingSpots.number;
    const flexPrice = onlineBookingFormData.calculatedFlexPrice?.text;

    const zoneNameInfo =
        productSelection.Zone !== bookingFlowContext.facility.name
            ? productSelection.Zone
            : null;
    const productDescription = productSelection.Description ?? null;

    const accessDevicesQuantity = accessDevices.length
        ? accessDevices
              .map((device) => device.Quantity)
              .reduce((prev, next) => +prev + +next)
        : null;
    const paymentSelectionText = paymentSelection?.value
        ? formLang[paymentSelection?.value.toLowerCase()]
        : paymentSelection.text;

    const listSelectionRows = [
        { left: formLang.facility, right: bookingFlowContext.facility.name },

        zoneNameInfo
            ? { left: formLang.zonename, right: productSelection.Zone }
            : {},

        {
            left: formLang.product,
            right: productSelection.OverviewText ?? productSelection.Text,
        },

        productDescription
            ? {
                  left: formLang.descriptionText,
                  right: productSelection.Description,
              }
            : {},

        insuranceFeeId
            ? {
                  left: productSelection.CancellationInsuranceFee.Heading,
                  right: formLang.insuranceFeeActive,
              }
            : {},

        accessDevicesQuantity
            ? {
                  left: formLang.accessDevice.numberOfAD,
                  right: accessDevicesQuantity,
              }
            : {},

        isFlexbookingProduct
            ? {
                  left: `${formLang.flexstartdate} ${formLang.flexandtime}`,
                  right:
                      parseDate(startDateSelection, selectedLocale, "L") +
                      " " +
                      startTimeSelection,
              }
            : {
                  left: formLang.startDate,
                  right: parseDate(startDateSelection, selectedLocale, "L"),
              },

        isFlexbookingProduct
            ? {
                  left: `${formLang.flexenddate} ${formLang.flexandtime}`,
                  right:
                      parseDate(endDateSelection, selectedLocale, "L") +
                      " " +
                      endTimeSelection,
              }
            : {},

        { left: formLang.numberOfParkingSpots, right: numberOfSpots },

        spotSelectionFormData.length > 0
            ? {
                  left:
                      spotSelectionFormData.length > 1
                          ? formLang.parkingspots
                          : formLang.parkingspot,
                  right: selectedSpotsList,
              }
            : {},

        vehiclesFormData && vehiclesFormData.length > 0
            ? {
                  left:
                      vehiclesFormData.length > 1
                          ? formLang.vehicles
                          : formLang.vehicle,
                  right: vehicleLicensePlateNumbers.replace(/;/g, ", "),
                  class: "uppercase",
              }
            : {},

        {
            left: formLang.payment,
            right:
                formLang.productType === 1
                    ? paymentSelectionText
                    : formLang.creditcard,
        },
        {
            left: formLang.termination,
            right: formLang.terminationInfo,
        },

        bookingFlowContext.availableSteps.documentUpload?.fileMeta?.name
            ? {
                  left: formLang.attachment || "Attachment",
                  right: bookingFlowContext.availableSteps.documentUpload.fileMeta.name,
              }
            : {},
    ];

    const listPaymentProductRows = [
        {
            left: formLang.product,
            right: productSelection.OverviewText ?? productSelection.Text,
            class: "loading",
        },
        insuranceFeeId
            ? {
                  left: productSelection.CancellationInsuranceFee.Heading,
                  right: `${productSelection.CancellationInsuranceFee.Price.replace(".", ",")} ${productSelection.Currency}`,
                  class: "loading",
              }
            : {},
        {
            left: `${formLang.total} (${paymentSelectionText})`,
            right: "-",
            class: "bold bordertop loading",
        },
        {
            left: `${formLang.subtotal}`,
            right: "-",
            class: "small loading",
        },
        {
            left: `${formLang.vat} `,
            right: "-",
            class: "small loading",
        },
    ];

    const [pending, setPending] = useState(true);
    const [error, setError] = useState(null);
    const [selectionRows, setSelectionRows] = useState(listSelectionRows);
    const [paymentProductRows, setPaymentProductRows] = useState(
        listPaymentProductRows,
    );
    const [termsAndConditions, setTermsAndConditions] = useState({
        selected: false,
        required: true,
        elementType: "checkbox",
    });
    const [promotion, setPromotion] = useState(null);
    const [promotionValid, setPromotionValid] = useState(null);

    const [quoteUid, setQuoteUid] = useState(
        bookingFlowContext.availableSteps.onlineBooking.orderLineUid,
    );
    const [quoteNumber, setQuoteNumber] = useState(null);
    const [updatedQuote, setUpdatedQuote] = useState(null);
    const [withPromotion, setWithPromotion] = useState(null);
    const [totalPrice, setTotalPrice] = useState(null);
    const [paymentUrl, setPaymentUrl] = useState(null);
    const [currentQuoteSessionKey, setCurrentQuoteSessionKey] = useState(null);
    const previousQuoteSessionKey = props.currentQuoteSessionKey ?? null;
    const [registerNewPayment, setRegisterNewPayment] = useState(false);
    const [currentPromotionCode, setCurrentPromotionCode] = useState(null);
    const order = bookingFlowContext.availableSteps.onlineBooking.order;
    const [showCancelLightbox, setShowCancelLightbox] = useState(false);
    const [serviceFee, setServiceFee] = useState(null);

    const closeCancelLightbox = () => {
        setShowCancelLightbox(false);

        const urlParams = new URLSearchParams(window.location.search);
        urlParams.delete("responseCode");
        urlParams.delete("transactionId");
        urlParams.delete("qId");

        const newUrl =
            window.location.origin +
            window.location.pathname
                .replace("/PaymentResult", "/")
                .replace("//", "/") +
            "?" +
            urlParams.toString();

        history.pushState(paramsToObject(urlParams), document.title, newUrl);
    };

    useEffect(() => {
        if (!order) return;

        let isSubscribed = true;

        const getCalculatedPrice = async () => {
            setPending(true);
            setError(null);
            setPaymentProductRows(listPaymentProductRows);

            const selectedSpotsIdList =
                spotSelectionFormData.length > 0
                    ? spotSelectionFormData
                          .map((item) => item.item.ParkingSpace.Uid)
                          .join(";")
                    : "";
            const selectedSpotsNameList =
                spotSelectionFormData.length > 0
                    ? spotSelectionFormData
                          .map((item) => item.item.ParkingSpace.Name ?? "")
                          .join(";")
                    : "";

            let flexBookingParams = isFlexbookingProduct
                ? {
                      StartTime: startTimeSelection,
                      EndDate: parseDate(endDateSelection, "sv", "L"),
                      EndTime: endTimeSelection,
                      FacilityId: bookingFlowContext.facility.id,
                      ProductType: formLang.productType,
                  }
                : {
                      FacilityId: bookingFlowContext.facility.id,
                      ProductType: formLang.productType,
                  };

            let flexBookingProductParams = isFlexbookingProduct
                ? { Price: flexPrice }
                : {};

            let quoteForm = {
                ProcessType: productSelection.ProcessType,
                StartDate: parseDate(startDateSelection, "sv", "L"),
                ...flexBookingParams,
                Products: [
                    {
                        UID: productSelection.PhysicalZoneId,
                        Product_Options: [
                            {
                                ProductOption: productSelection.Id,
                                Quantity: numberOfSpots,
                                LicensePlateNumber: vehicleLicensePlateNumbers,
                                LicensePlateDescription: vehicleDescriptions,
                                BillingFrequency: paymentSelectionValue,
                                Spot_Id: selectedSpotsIdList,
                                Spot_Name: selectedSpotsNameList,
                                ...flexBookingProductParams,
                            },
                        ],
                    },
                ],
            };
            const quoteSessionKey = `__aimo-booking-flow__quote__${order.orderId}-${numberOfSpots}-${insuranceFeeId ?? 0}-${selectedSpotsList.replace(/, /g, "-")}-${paymentSelectionValue}-${quoteForm.StartDate}-${quoteForm.EndDate ?? "N/A"}-${quoteForm.StartTime ?? "N/A"}-${quoteForm.EndTime ?? "N/A"}-${vehicleLicensePlateNumbers.replace(/;/g, "-")}${vehicleDescriptions.length > 0 ? "-" + vehicleDescriptions.replace(/;/g, "-") : ""}${accessDeviceKeys}`;

            setCurrentQuoteSessionKey(quoteSessionKey);

            const OrderId = order.orderId;
            const ProcessType =
                onlineBookingFormData.productSelection.item.ProcessType;

            //No registration plates data if skidata permit usage type (CPS-24723)
            const isSkidata =
                bookingFlowContext?.availableSteps?.onlineBooking?.formData?.productSelection?.item?.PermitUsageType?.toLowerCase() ===
                "skidata";
            let Data = {};

            if (!isSkidata) {
                const RegistrationPlates =
                    vehiclesFormData.length > 0
                        ? vehiclesFormData.map((item) => ({
                              PlateText: item.PlateText.trim()
                                  .split(" ")
                                  .join(""),
                              CountryCode: item.CountryCode,
                          }))
                        : [];
                Data = { RegistrationPlates };
            }

            const updateSalesOrderParams = { OrderId, ProcessType, Data };

            const createQuote = !updatedQuote
                ? await products.updateSalesOrder({
                      form: updateSalesOrderParams,
                      bookingflowPage:
                          bookingFlowContext.episerver.bookingflowUrl,
                  })
                : updatedQuote;

            if (createQuote.status === 200 && isSubscribed) {
                const promoCfg = appliedPromotionConfig(
                    bookingFlowContext,
                    createQuote.body.OrderLines?.[0]?.Promotion,
                );

                const _StartAfter = promoCfg?.StartAfter ?? null;
                const _Duration = promoCfg?.Duration ?? null;

                const withMonthlyPromo = isMonthsFreeOrHalfPricePromotion(
                    createQuote.body.OrderLines?.[0]?.Promotion,
                    promoCfg,
                );

                const withPromo =
                    createQuote.body.OrderLines &&
                    createQuote.body.OrderLines[0].Promotion &&
                    !withMonthlyPromo
                        ? !!createQuote.body.OrderLines[0].Promotion
                        : false;

                const StartAfter = withMonthlyPromo ? _StartAfter : null;
                const Duration = withMonthlyPromo ? _Duration : null;

                const Discount =
                    createQuote.body.OrderLines &&
                    createQuote.body.OrderLines[0].Promotion &&
                    createQuote.body.OrderLines[0].Promotion.Percent
                        ? createQuote.body.OrderLines[0].Promotion.Percent
                        : null;
                const ValidFrom =
                    withMonthlyPromo &&
                    createQuote.body.OrderLines[0].Promotion.Validity.ValidFrom
                        ? parseDate(
                              createQuote.body.OrderLines[0].Promotion.Validity
                                  .ValidFrom,
                              "sv",
                              "L",
                          )
                        : null;
                const ValidTo =
                    withMonthlyPromo &&
                    createQuote.body.OrderLines[0].Promotion.Validity.ValidTo
                        ? parseDate(
                              createQuote.body.OrderLines[0].Promotion.Validity
                                  .ValidTo,
                              "sv",
                              "L",
                          )
                        : null;

                setWithPromotion(withPromo || withMonthlyPromo);
                setCurrentPromotionCode(
                    withPromo &&
                        !createQuote.body.OrderLines[0]?.Promotion
                            ?.AddAutomatically
                        ? createQuote.body.OrderLines[0]?.Promotion?.PromoCode
                        : null,
                );
                setPromotion(
                    withPromo || withMonthlyPromo
                        ? {
                              ...createQuote.body.OrderLines[0]?.Promotion,
                              StartAfter,
                              Duration,
                          }
                        : null,
                );
                setPromotionValid(
                    ValidFrom && ValidTo ? ` (${ValidFrom}–${ValidTo})` : "",
                );
                setQuoteNumber(createQuote.body.Uid);
                setPending(false);

                const totalProductAmount =
                    (withPromo
                        ? createQuote.body.OrderLines[0]
                              .ListUnitPriceWithPromotion
                        : createQuote.body.OrderLines[0].ListUnitPrice) / 100;
                const totalProductAmountPrevious = withPromo
                    ? (createQuote?.body.OrderLines[0].ListUnitPrice ||
                          createQuote.body.OrderLines[0].ListUnitPrice) / 100
                    : null;

                const totalAmountWithoutTax =
                    (withPromo
                        ? createQuote.body.OrderLines[0]
                              .LineTotalWithoutVatWithPromotion
                        : createQuote.body.OrderLines[0].LineTotalWithoutVat) /
                    100;
                const totalVatAmount =
                    (withPromo
                        ? createQuote.body.OrderLines[0]
                              .LineVatAmountWithPromotion
                        : createQuote.body.OrderLines[0].LineVatAmount) / 100;

                const totalNetAmount =
                    (withPromo
                        ? createQuote.body.OrderLines[0].LineTotalWithPromotion
                        : createQuote.body.OrderLines[0].LineTotal) / 100;
                const totalNetAmountPrevious = withPromo
                    ? (createQuote?.body.OrderLines[0].LineTotal ||
                          createQuote?.body.OrderLines[0].LineTotal) / 100
                    : null;

                const totalFullPromoPeroidWithoutPromo = withMonthlyPromo
                    ? (createQuote?.body.OrderLines[0].ListUnitPrice *
                          (StartAfter + Duration)) /
                      100
                    : null;
                const totalFullPromoPeroidWithPromo = withMonthlyPromo
                    ? (createQuote?.body.OrderLines[0].ListUnitPrice *
                          StartAfter +
                          (Discount && Discount !== 100
                              ? ((Duration * Discount) / 100) *
                                createQuote?.body.OrderLines[0].ListUnitPrice
                              : 0)) /
                      100
                    : null;

                const accessDevicesRows = accessDevices?.length
                    ? createQuote?.body.Product[0].ProductOption[0].NestedProductOptions.map(
                          (option) => {
                              return {
                                  left:
                                      (option.Quantity > 1
                                          ? Math.round(option.Quantity) + "× "
                                          : "") + option.ProductName,
                                  right: `${formatCurrency(option.NetTotal, true)}`,
                              };
                          },
                      )
                    : [{}];

                const serviceFee = createQuote.body.OrderLines[0].ServiceFeeAmount / 100 ?? null;

                setTotalPrice(totalNetAmount);
                setPaymentUrl(createQuote.body.PaymentUrl);
                setServiceFee(serviceFee);

                setPaymentProductRows([
                    withMonthlyPromo
                        ? {
                              left: formLang.promomonthfree.originalprice.replace(
                                  "{0}",
                                  StartAfter + Duration,
                              ),
                              right: formatCurrency(
                                  totalFullPromoPeroidWithoutPromo,
                                  true,
                              ),
                              class: "bold strike",
                          }
                        : {},
                    withMonthlyPromo
                        ? {
                              left: formLang.totalwithdiscount,
                              right: formatCurrency(
                                  totalFullPromoPeroidWithPromo,
                                  true,
                              ),
                              class: "bold",
                          }
                        : {},
                    totalProductAmount
                        ? {
                              left:
                                  productSelection.OverviewText ??
                                  productSelection.Text,
                              right: totalProductAmountPrevious
                                  ? [
                                        formatCurrency(
                                            totalProductAmountPrevious,
                                            true,
                                        ),
                                        formatCurrency(
                                            totalProductAmount,
                                            true,
                                        ),
                                    ]
                                  : formatCurrency(totalProductAmount, true),
                          }
                        : {},
                    insuranceFeeId
                        ? {
                              left: productSelection.CancellationInsuranceFee
                                  .Heading,
                              right: `${productSelection.CancellationInsuranceFee.Price.replace(".", ",")} ${productSelection.Currency}`,
                          }
                        : {},
                    ...accessDevicesRows,
                    {
                        left: `${formLang.total} (${paymentSelectionText})`,
                        right:
                            formatCurrency(
                                totalNetAmountPrevious
                                    ? totalNetAmountPrevious
                                    : totalNetAmount,
                                true,
                            ) + (totalNetAmountPrevious ? "" : "*"),
                        class: totalNetAmountPrevious
                            ? "bold bordertop strike"
                            : "bold bordertop",
                    },
                    totalNetAmountPrevious
                        ? {
                              left: formLang.totalwithdiscount,
                              right: formatCurrency(totalNetAmount, true) + "*",
                              class: "bold",
                          }
                        : {},
                    {
                        left: `${formLang.subtotal} `,
                        right: formatCurrency(totalAmountWithoutTax, true),
                        class: "small",
                    },
                    {
                        left: `${formLang.vat} `,
                        right: formatCurrency(totalVatAmount, true),
                        class: "small",
                    },
                ]);

                sessionStorage.setItem(
                    quoteSessionKey,
                    JSON.stringify(createQuote),
                );
                if (
                    previousQuoteSessionKey &&
                    previousQuoteSessionKey !== quoteSessionKey
                )
                    sessionStorage.removeItem(previousQuoteSessionKey);
            } else if (isSubscribed) {
                setPending(false);

                setPaymentProductRows([
                    {
                        left:
                            productSelection.OverviewText ??
                            productSelection.Text,
                        right: "-",
                    },
                    insuranceFeeId
                        ? {
                              left: productSelection.CancellationInsuranceFee
                                  .Heading,
                              right: "-",
                          }
                        : {},
                    {
                        left: `${formLang.total} (${paymentSelectionText})`,
                        right: "-",
                        class: "bold border bordertop",
                    },
                    {
                        left: `${formLang.subtotal}`,
                        right: "-",
                        class: "small",
                    },
                    {
                        left: `${formLang.vat} `,
                        right: "-",
                        class: "small",
                    },
                ]);

                setError({
                    error: true,
                    errorText: createQuote.errorMessage,
                    errorType: "createQuote",
                });
            }
        };
        getCalculatedPrice();

        return () => (isSubscribed = false);
    }, [order,  updatedQuote]);

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const responseCode = urlParams.get("responseCode");

        if (!responseCode) return;

        setShowCancelLightbox(true);
        setPaymentUrl(null);

        setRegisterNewPayment(true);
    }, []);

    useEffect(() => {
        let isSubscribed = true;

        const getNewPaymentUrl = async () => {
            if (
                !quoteUid ||
                !quoteNumber ||
                !totalPrice ||
                !currentQuoteSessionKey ||
                !registerNewPayment
            )
                return;

            setPending(true);

            const quoteConfirmationForm = {
                facilityId: bookingFlowContext.facility.id,
                productType: formLang.productType,
                processType: productSelection.ProcessType,
                quoteId: quoteUid,
                quoteNumber: quoteNumber,
                amount: totalPrice,
            };
            const creditcardPaymentResponse =
                await payment.getCreditcardPaymentUrl(
                    {
                        form: quoteConfirmationForm,
                        bookingflowPage:
                            bookingFlowContext.episerver.bookingflowUrl,
                    },
                    bookingFlowContext.useMockedData,
                );
            if (creditcardPaymentResponse.status === 200) {
                const responseUrl = decodeURIComponent(
                    creditcardPaymentResponse.body
                        .replace(/"/g, "")
                        .replace(/\\u0026/g, "&"),
                );
                setPaymentUrl(responseUrl);

                let quoteSessionValue = sessionStorage.getItem(
                    currentQuoteSessionKey,
                );

                if (quoteSessionValue) {
                    quoteSessionValue = JSON.parse(quoteSessionValue);
                    quoteSessionValue.body.PaymentUrl = responseUrl;

                    sessionStorage.setItem(
                        currentQuoteSessionKey,
                        JSON.stringify(quoteSessionValue),
                    );
                }
            } else {
                setPaymentUrl(null);
            }

            setPending(false);
        };
        getNewPaymentUrl();

        return () => (isSubscribed = false);
    }, [
        quoteUid,
        quoteNumber,
        totalPrice,
        currentQuoteSessionKey,
        registerNewPayment,
    ]);

    // `disabled` is inert on an anchor, so the href has to be dropped to actually block the click.
    const stpPaymentUrl = attachmentMissing ? undefined : paymentUrl;

    const quoteUpdateEventHandler = (promoCode, newQuote) => {
        setCurrentPromotionCode(promoCode);
        setUpdatedQuote(newQuote);
    };

    const placeLtpOrderHandler = async () => {
        setPending(true);
        setError(null);

        if (attachmentMissing) {
            setError({
                error: true,
                errorText: formLang.attachmentrequired,
                errorType: "confirmQuote",
            });
            setPending(false);
            return;
        }

        const newQuoteForm = {
            termsAndConditions: {
                value: termsAndConditions,
                setValue: setTermsAndConditions,
            },
        };

        let [formValidationError, cleanFormData] = validateFormObject(
            newQuoteForm,
            bookingFlowContext.localization.global.errorMessages,
        );
        if (formValidationError) {
            setPending(false);
            return;
        }

        const quoteConfirmationForm = {
            AutomaticOnlineOrderActivation:
                productSelection.AutomaticOnlineOrderActivation,
            Uid: order.orderId,
        };

        const confirmQuote = await products.confirmSalesOrder(
            {
                form: quoteConfirmationForm,
                bookingflowPage: bookingFlowContext.episerver.bookingflowUrl,
            },
            bookingFlowContext.useMockedData,
        );

        if (confirmQuote.status === 200) {
            setPending(false);
            setBookingFlowContext({
                ...bookingFlowContext,
                activeStep:
                    bookingFlowContext.availableSteps.orderConfirmation.index,
                availableSteps: {
                    ...bookingFlowContext.availableSteps,
                    orderOverview: {
                        ...bookingFlowContext.availableSteps.orderOverview,
                        formData: {
                            ...cleanFormData,
                            ...quoteConfirmationForm,
                        },
                    },
                },
            });
        } else {
            setError({
                error: true,
                errorText: confirmQuote.body.ErrorMessage,
                errorType: "confirmQuote",
            });
            setPending(false);
        }
    };

    return (
        <div className="bookingflowpage__app bookingflowpage__app--orderoverview">
            <BackNavigation disabled={pending} />
            <Heading />

            <div className="single-step single-step--overview">
                {showCancelLightbox && (
                    <Lightbox onCloseEvent={closeCancelLightbox} close="Close">
                        <div className="single-step__lightbox__heading">
                            {formLang.paymentcancelled.heading}
                        </div>
                        <div className="single-step__lightbox__text">
                            {formLang.paymentcancelled.text}
                        </div>
                        <div className="single-step__lightbox__options">
                            <button
                                className="cancel"
                                onClick={() =>
                                    (window.location = formLang.completionLink)
                                }
                            >
                                {formLang.paymentcancelled.links.startpage}
                            </button>
                            <button onClick={closeCancelLightbox}>
                                {formLang.paymentcancelled.links.continue}
                            </button>
                        </div>
                    </Lightbox>
                )}

                <h2>
                    {
                        bookingFlowContext.localization.orderOverview
                            .selectionHeading
                    }
                </h2>
                <List rows={selectionRows} />

                <OrderOverviewDiscount
                    disabled={quoteNumber === null}
                    quoteId={quoteNumber}
                    currentPromotionCode={currentPromotionCode}
                    processType={productSelection.ProcessType}
                    formLang={formLang}
                    pending={pending}
                    setPending={setPending}
                    error={error}
                    setError={setError}
                    quoteUpdateEvent={quoteUpdateEventHandler}
                />

                <div className="single-step__yellow-wrapper">
                    <h2>
                        {
                            bookingFlowContext.localization.orderOverview
                                .paymentHeading
                        }
                    </h2>
                    {error &&
                        error.errorText?.length > 0 &&
                        error.errorType === "createQuote" && (
                            <Error
                                text={error.errorText}
                                modifierClass="blue"
                            />
                        )}
                    <List
                        rows={paymentProductRows}
                        class="single-step__list--smallmargin"
                    />
                    {withPromotion && promotion && (
                        <p className="extrasmall nomargin margintop italic">
                            {`*${currentPromotionCode ? promotion.Name + " - " : ""}${formLang.promodiscount} ${Math.round(promotion.Percent || promotion.Value / 100)}${promotion.Value ? " " + productSelection.Currency : "\u202F%"}`}
                            {promotion.Validity?.ValidFrom &&
                                !promotion.StartAfter &&
                                ` ${formLang.promovalid.toLowerCase()}${promotion.Validity?.ValidTo ? "" : " " + formLang.promofrom.split(" ")[0]} ${parseDate(promotion.Validity.ValidFrom, selectedLocale, "L")}${promotion.Validity?.ValidTo ? "–" + parseDate(promotion.Validity.ValidTo, selectedLocale, "L") : ""}`}
                            {promotion.StartAfter > 0 &&
                                promotion.Percent !== 100 &&
                                promotion.Percent !== 50 &&
                                ` ${formLang.promovalid.toLowerCase()}${promotion.Duration ? (promotion.Duration > 1 ? " " + formLang.promodurations.replace("{0}", promotion.Duration) : " " + formLang.promoduration.replace("{0}", promotion.Duration)) : ""} ${formLang.promofrom} ${promotion.StartAfter + 1}`}
                            {promotion.StartAfter > 0 &&
                                (promotion.Percent === 100 ||
                                    promotion.Percent === 50) &&
                                ` ${promotion.Duration > 1 ? formLang.promomonthfree.footnotemultipleduration.replace("{0}", promotion.StartAfter + 1 + "–" + (promotion.StartAfter + promotion.Duration)) : formLang.promomonthfree.footnotesingleduration.replace("{0}", promotion.StartAfter + 1)}${promotionValid ? promotionValid : ""}`}
                        </p>
                    )}

                    {bookingFlowContext.country === "se" && (
                        <div className="single-step__information">
                            {serviceFee ? (
                                <p className="extrasmall nomargin margintop italic">
                                    *{formLang.servicefeeincl.replace("{1}", `${serviceFee} ${productSelection?.Currency}`)}
                                </p>
                            ): ''}
                            {!bookingFlowContext.episerver.PaymentInfoUrl && (
                                <p className="extrasmall nomargin margintop italic">
                                    {withPromotion && promotion ? "" : "*"}
                                    {formLang.invoicefeenotincl}
                                </p>
                            )}
                            {bookingFlowContext.episerver.PaymentInfoUrl && (
                                <a
                                    href={
                                        bookingFlowContext.episerver
                                            .PaymentInfoUrl
                                    }
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {" "}
                                    {withPromotion && promotion ? "" : "*"}
                                    {formLang.paymentinfo}
                                </a>
                            )}
                        </div>
                    )}
                </div>

                <h2>{formLang.termsandconditionsheading}</h2>
                <div className="single-step__checkbox-wrapper">
                    <div>
                        <CheckboxWithLabel
                            text={
                                formLang.productType === 1
                                    ? bookingFlowContext.localization
                                          .createAccount.termsAndConditionsLTP
                                    : bookingFlowContext.localization
                                          .createAccount.termsAndConditionsSTP
                            }
                            value={termsAndConditions}
                            setValue={setTermsAndConditions}
                        />
                    </div>
                </div>

                {error &&
                    error.errorText?.length > 0 &&
                    error.errorType === "confirmQuote" && (
                        <Error text={error.errorText} />
                    )}

                {attachmentMissing && <Error text={formLang.attachmentrequired} />}

                {formLang.productType === 1 && (
                    <button
                        className="single-step__button single-step__button--order"
                        data-booking-flow-step="ltp-orderoverview"
                        disabled={
                            (error && error.errorType !== "updatedQuote") ||
                            pending ||
                            !termsAndConditions.selected ||
                            attachmentMissing
                        }
                        aria-busy={pending}
                        onClick={placeLtpOrderHandler}
                    >
                        {
                            bookingFlowContext.localization.orderOverview
                                .buttons.placeOrder
                        }
                    </button>
                )}
                {formLang.productType === 2 && (
                    <div className="single-step__link-wrapper">
                        <a
                            className="single-step__button single-step__button--order"
                            data-booking-flow-step="stp-orderoverview"
                            disabled={
                                (error && error.errorType !== "updatedQuote") ||
                                pending ||
                                !termsAndConditions.selected ||
                                !paymentUrl ||
                                (totalPrice && totalPrice === "0.00") ||
                                attachmentMissing
                            }
                            aria-busy={pending}
                            href={stpPaymentUrl}
                        >
                            {
                                bookingFlowContext.localization.orderOverview
                                    .buttons.placeStpOrder
                            }
                        </a>
                    </div>
                )}
            </div>
        </div>
    );
};

export default OrderOverview;
